import { $createParagraphNode, $createTextNode, ElementNode } from "lexical";

import { $createLinkNode } from "@lexical/link";
import { $createListItemNode, $createListNode } from "@lexical/list";
import { $createHeadingNode } from "@lexical/rich-text";

import { LexicalNode } from "components/ContentEditor/converter/types";

// Helper to apply format flags to a text node
export const applyFormatToTextNode = (textNode: ReturnType<typeof $createTextNode>, format: number | undefined): void => {
	if (!format || format === 0 || typeof format !== "number") return;
	if (format & 1) textNode.toggleFormat("bold");
	if (format & 2) textNode.toggleFormat("italic");
};

// Helper to process children nodes and append them to a parent element
const processNodeChildren = (nodeChildren: LexicalNode[], parentElement: ElementNode): void => {
	if (!nodeChildren || !Array.isArray(nodeChildren)) return;

	for (const child of nodeChildren) {
		try {
			if (child.type === "text") {
				const textNode = $createTextNode(child.text as string);
				applyFormatToTextNode(textNode, child.format as number);
				parentElement.append(textNode);
			} else if (child.type === "link" && child.url) {
				const linkNode = $createLinkNode(child.url as string, {
					target: child.target as string,
					rel: child.rel as string,
				});

				if (child.children && child.children[0]) {
					const linkText = $createTextNode((child.children[0] as LexicalNode).text as string);
					applyFormatToTextNode(linkText, (child.children[0] as LexicalNode).format as number);
					linkNode.append(linkText);
				}
				parentElement.append(linkNode);
			} else if (child.type === "image" && child.src) {
				const imgPlaceholder = $createTextNode(`[Image: ${child.altText || "image"}]`);
				parentElement.append(imgPlaceholder);
			}
		} catch (err) {
			console.error("Error processing node child:", err);
		}
	}
};

// Helper to create a heading block
export const createHeadingBlock = (block: LexicalNode): ReturnType<typeof $createHeadingNode> => {
	const headingNode = $createHeadingNode((block.level as "h1" | "h2" | "h3") || "h2");
	processNodeChildren(block.children ?? [], headingNode);
	return headingNode;
};

// Helper to create a paragraph block
export const createParagraphBlock = (block: LexicalNode): ReturnType<typeof $createParagraphNode> => {
	const paragraphNode = $createParagraphNode();
	processNodeChildren(block.children ?? [], paragraphNode);
	return paragraphNode;
};

// Helper to process list item content (descend into paragraph if present)
const processListItemContent = (listItem: LexicalNode, itemElement: ReturnType<typeof $createListItemNode>): void => {
	if (listItem.children && Array.isArray(listItem.children)) {
		for (const contentNode of listItem.children) {
			if (contentNode.type === "paragraph" && contentNode.children) {
				processNodeChildren(contentNode.children, itemElement);
			}
		}
	}
};

// Helper to create a list block
export const createListBlock = (block: LexicalNode, onListCreated?: (list: ReturnType<typeof $createListNode>) => void): ReturnType<typeof $createListNode> => {
	const listNode = $createListNode((block.listType as "bullet" | "number") || "bullet");

	if (block.children && Array.isArray(block.children)) {
		for (const listItem of block.children) {
			try {
				const item = $createListItemNode();
				processListItemContent(listItem, item);
				listNode.append(item);
			} catch (err) {
				console.error("Error processing list item:", err);
			}
		}
	}

	onListCreated?.(listNode);
	return listNode;
};
